/**
 * GDPR "export ALL my data" (owner, 16.10.2026), the part only this device
 * has: since Plans v2, Free and Basic keep boards and documents ON THE DEVICE
 * (`device:<uid>`), where the server's export (`GET /auth/export`) cannot see
 * them — and the server's `documents` category covers synced documents only,
 * not boards. This collects every own record of the extensions that hold the
 * user's content, from each own local scope, as stored:
 *
 *   { format, exportedAt, account: { id, email },
 *     scopes: { 'device:7': { documents: [...], 'task-manager': [...], … },
 *               'personal:7': { … },     // the local copy of the cloud scope
 *               'personal:local': { … } } }  // signed-out drafts
 *
 * Team scopes are left out on purpose: team data is the team's (decision 4).
 * Desktop only; read-only (it lists, it never writes).
 */
import { listRecords } from '../records/recordsClient';
import { useAuthStore } from '../store/authStore';
import { isTauri } from '../utils/isTauri';

/** The extensions whose records are the user's own content. */
export const LOCAL_EXPORT_EXTENSIONS = Object.freeze(['documents', 'task-manager', 'calendar', 'projects']);

export const LOCAL_EXPORT_FORMAT = 'apiboo-device-export/1';

/** The own local scopes of the signed-in account (plus signed-out drafts). */
export function ownLocalScopes(userId) {
  const scopes = ['personal:local'];
  if (userId != null) scopes.unshift(`device:${userId}`, `personal:${userId}`);
  return scopes;
}

export async function collectLocalData() {
  if (!isTauri()) throw new Error('desktop_only');
  const { user } = useAuthStore.getState();
  const scopes = {};
  for (const workspaceId of ownLocalScopes(user?.id)) {
    const byExt = {};
    for (const extensionId of LOCAL_EXPORT_EXTENSIONS) {
      // eslint-disable-next-line no-await-in-loop -- one small local read each, in order
      const rows = await listRecords({ workspaceId, extensionId });
      if (Array.isArray(rows) && rows.length) byExt[extensionId] = rows;
    }
    if (Object.keys(byExt).length) scopes[workspaceId] = byExt;
  }
  return {
    format: LOCAL_EXPORT_FORMAT,
    exportedAt: new Date().toISOString(),
    account: user?.id != null ? { id: user.id, email: user.email ?? null } : null,
    scopes,
  };
}
